import { useEffect, useState } from "react";

import { CompetitionDTO } from "types";

// Category information type
export type CategoryInfo = {
    name: string;
    color: string;
    icon: string;
    type:
        | "art_style"
        | "subject"
        | "character"
        | "environment"
        | "general"
        | "holiday";
};

// Type display information
export const categoryTypeInfo: Record<
    string,
    { icon: string; color: string; label: string }
> = {
    art_style: { icon: "🎨", color: "bg-blue-500", label: "Art Styles" },
    subject: { icon: "📷", color: "bg-amber-500", label: "Subjects" },
    character: { icon: "👤", color: "bg-red-500", label: "Characters" },
    environment: { icon: "🏞️", color: "bg-green-600", label: "Environments" },
    general: { icon: "🎭", color: "bg-slate-500", label: "General" },
    holiday: { icon: "🎄", color: "bg-red-500", label: "Holidays" },
};

// Utility function to determine category
export const getCompetitionCategory = (title: string): CategoryInfo => {
    title = title.toLowerCase();

    // Art Style Categories
    if (title.includes("watercolour"))
        return {
            name: "Watercolour",
            color: "bg-sky-400",
            icon: "🎨",
            type: "art_style",
        };
    if (title.includes("comic"))
        return {
            name: "Comic",
            color: "bg-pink-500",
            icon: "💬",
            type: "art_style",
        };
    if (title.includes("anime") || title.includes("manga"))
        return {
            name: "Anime",
            color: "bg-fuchsia-500",
            icon: "🇯🇵",
            type: "art_style",
        };
    if (title.includes("pixel"))
        return {
            name: "Pixel",
            color: "bg-violet-600",
            icon: "👾",
            type: "art_style",
        };
    if (title.includes("painterly"))
        return {
            name: "Painterly",
            color: "bg-amber-600",
            icon: "🖌️",
            type: "art_style",
        };
    if (title.includes("oil"))
        return {
            name: "Oil",
            color: "bg-yellow-700",
            icon: "🎭",
            type: "art_style",
        };
    if (title.includes("pastel"))
        return {
            name: "Pastel",
            color: "bg-pink-300",
            icon: "🌸",
            type: "art_style",
        };
    if (title.includes("minimalist"))
        return {
            name: "Minimalist",
            color: "bg-gray-500",
            icon: "◻️",
            type: "art_style",
        };
    if (title.includes("neoclassical"))
        return {
            name: "Neoclassical",
            color: "bg-amber-800",
            icon: "🏛️",
            type: "art_style",
        };
    if (title.includes("nouveau"))
        return {
            name: "Art Nouveau",
            color: "bg-emerald-600",
            icon: "🌿",
            type: "art_style",
        };
    if (title.includes("baroque"))
        return {
            name: "Baroque",
            color: "bg-yellow-600",
            icon: "👑",
            type: "art_style",
        };
    if (title.includes("surreal"))
        return {
            name: "Surreal",
            color: "bg-purple-500",
            icon: "🌀",
            type: "art_style",
        };
    if (title.includes("abstract"))
        return {
            name: "Abstract",
            color: "bg-blue-500",
            icon: "🔷",
            type: "art_style",
        };
    if (title.includes("retro"))
        return {
            name: "Retro",
            color: "bg-orange-500",
            icon: "📻",
            type: "art_style",
        };
    if (title.includes("cartoon") || title.includes("chibi"))
        return {
            name: "Cartoon",
            color: "bg-emerald-600",
            icon: "📺",
            type: "art_style",
        };
    if (title.includes("gothic"))
        return {
            name: "Gothic",
            color: "bg-slate-800",
            icon: "🏰",
            type: "art_style",
        };
    if (title.includes("realistic"))
        return {
            name: "Realistic",
            color: "bg-slate-600",
            icon: "📷",
            type: "art_style",
        };

    // Holiday Categories
    if (title.includes("easter"))
        return {
            name: "Easter",
            color: "bg-orange-600",
            icon: "🐣",
            type: "holiday",
        };
    if (title.includes("halloween"))
        return {
            name: "Halloween",
            color: "bg-purple-500",
            icon: "🎃",
            type: "holiday",
        };
    if (title.includes("christmas"))
        return {
            name: "Christmas",
            color: "bg-red-500",
            icon: "🎄",
            type: "holiday",
        };
    if (title.includes("valentine"))
        return {
            name: "Valentine's Day",
            color: "bg-pink-500",
            icon: "💖",
            type: "holiday",
        };
    if (title.includes("st. patrick's day"))
        return {
            name: "St. Patrick's Day",
            color: "bg-green-500",
            icon: "🍀",
            type: "holiday",
        };
    if (title.includes("new year"))
        return {
            name: "New Year",
            color: "bg-blue-500",
            icon: "🎉",
            type: "holiday",
        };
    if (title.includes("mother's day"))
        return {
            name: "Mother's Day",
            color: "bg-pink-500",
            icon: "👩‍👧‍👦",
            type: "holiday",
        };
    if (title.includes("father's day"))
        return {
            name: "Father's Day",
            color: "bg-blue-500",
            icon: "👨‍👧‍👦",
            type: "holiday",
        };
    if (title.includes("memorial day"))
        return {
            name: "Memorial Day",
            color: "bg-red-500",
            icon: "🇺🇸",
            type: "holiday",
        };
    if (title.includes("may the 4th"))
        return {
            name: "May the 4th",
            color: "bg-blue-500",
            icon: "👾",
            type: "holiday",
        };

    // Main Subject Categories
    if (title.includes("character"))
        return {
            name: "Character",
            color: "bg-amber-500",
            icon: "👤",
            type: "subject",
        };
    if (title.includes("landscape"))
        return {
            name: "Landscape",
            color: "bg-emerald-600",
            icon: "🏞️",
            type: "environment",
        };
    if (title.includes("monster"))
        return {
            name: "Monster",
            color: "bg-red-500",
            icon: "👹",
            type: "character",
        };
    if (title.includes("portrait"))
        return {
            name: "Portrait",
            color: "bg-purple-500",
            icon: "🖼️",
            type: "subject",
        };
    if (title.includes("fantasy"))
        return {
            name: "Fantasy",
            color: "bg-indigo-500",
            icon: "🧙",
            type: "general",
        };
    if (title.includes("cover"))
        return {
            name: "Cover Art",
            color: "bg-blue-600",
            icon: "📚",
            type: "subject",
        };
    if (title.includes("trading card"))
        return {
            name: "Trading Card",
            color: "bg-violet-500",
            icon: "🃏",
            type: "subject",
        };

    // D&D Specific Categories
    if (title.includes("dragonborn"))
        return {
            name: "Dragonborn",
            color: "bg-red-600",
            icon: "🐲",
            type: "character",
        };
    if (title.includes("kobold"))
        return {
            name: "Kobold",
            color: "bg-amber-600",
            icon: "🦎",
            type: "character",
        };
    if (title.includes("dragon"))
        return {
            name: "Dragon",
            color: "bg-red-700",
            icon: "🐉",
            type: "character",
        };
    if (title.includes("paladin"))
        return {
            name: "Paladin",
            color: "bg-yellow-500",
            icon: "⚔️",
            type: "character",
        };
    if (title.includes("warlock"))
        return {
            name: "Warlock",
            color: "bg-purple-600",
            icon: "🔮",
            type: "character",
        };
    if (title.includes("druid"))
        return {
            name: "Druid",
            color: "bg-green-600",
            icon: "🍃",
            type: "character",
        };
    if (title.includes("barbarian"))
        return {
            name: "Barbarian",
            color: "bg-red-500",
            icon: "🪓",
            type: "character",
        };
    if (title.includes("ranger"))
        return {
            name: "Ranger",
            color: "bg-green-700",
            icon: "🏹",
            type: "character",
        };

    // Environmental Categories
    if (title.includes("arctic"))
        return {
            name: "Arctic",
            color: "bg-cyan-300",
            icon: "❄️",
            type: "environment",
        };
    if (title.includes("mountain"))
        return {
            name: "Mountain",
            color: "bg-slate-500",
            icon: "⛰️",
            type: "environment",
        };
    if (title.includes("lake"))
        return {
            name: "Lake",
            color: "bg-blue-400",
            icon: "🏞️",
            type: "environment",
        };
    if (title.includes("forest"))
        return {
            name: "Forest",
            color: "bg-green-800",
            icon: "🌲",
            type: "environment",
        };
    if (title.includes("jungle"))
        return {
            name: "Jungle",
            color: "bg-green-600",
            icon: "🌴",
            type: "environment",
        };
    if (title.includes("prehistoric"))
        return {
            name: "Prehistoric",
            color: "bg-amber-700",
            icon: "🦕",
            type: "general",
        };
    if (title.includes("spaceship"))
        return {
            name: "Spaceship",
            color: "bg-violet-700",
            icon: "🚀",
            type: "environment",
        };

    // Default if no specific category is found
    return { name: "Art", color: "bg-sky-500", icon: "🎭", type: "general" };
};

// Get human-readable label for a filter
export const getFilterLabel = (selectedCategory: string | null): string => {
    if (!selectedCategory) return "";

    if (selectedCategory.startsWith("type:")) {
        const typeKey = selectedCategory.replace("type:", "");
        return categoryTypeInfo[typeKey]?.label || typeKey;
    }

    return selectedCategory;
};

// Hook to analyze competitions and determine popular categories
export function useCompetitionCategories(competitions: CompetitionDTO[] = []) {
    const [popularCategories, setPopularCategories] = useState<CategoryInfo[]>([
        { name: "All", color: "bg-slate-500", icon: "🎭", type: "general" },
    ]);
    const [filterMode, setFilterMode] = useState<"category" | "type">(
        "category"
    );

    useEffect(() => {
        if (competitions.length === 0) return;

        // Analyze categories across all competitions
        const categoryCount: Record<string, number> = {};
        const categoryInfo: Record<string, CategoryInfo> = {};
        const typeCount: Record<string, number> = {};

        competitions.forEach((comp) => {
            const category = getCompetitionCategory(comp.title);

            // Track individual category frequency
            if (!categoryCount[category.name]) {
                categoryCount[category.name] = 0;
                categoryInfo[category.name] = category;
            }
            categoryCount[category.name]++;

            // Track category type frequency
            if (!typeCount[category.type]) {
                typeCount[category.type] = 0;
            }
            typeCount[category.type]++;
        });

        // If we have too many individual categories, switch to type-based filtering
        const uniqueCategories = Object.keys(categoryCount).length;

        if (uniqueCategories > 8) {
            setFilterMode("type");

            // Build type filters
            const typeFilters: CategoryInfo[] = [
                {
                    name: "All",
                    color: "bg-slate-500",
                    icon: "🎭",
                    type: "general",
                },
            ];

            // Add type filters that actually have competitions
            Object.keys(typeCount).forEach((type) => {
                if (typeCount[type] > 0 && type in categoryTypeInfo) {
                    typeFilters.push({
                        name: categoryTypeInfo[type].label,
                        color: categoryTypeInfo[type].color,
                        icon: categoryTypeInfo[type].icon,
                        type: type as any,
                    });
                }
            });

            setPopularCategories(typeFilters);
        } else {
            // Use individual category filters if we have a reasonable number
            setFilterMode("category");

            // Sort by frequency and take top categories (max 8 including "All")
            const topCategories = Object.keys(categoryCount)
                .sort((a, b) => categoryCount[b] - categoryCount[a])
                .slice(0, 7)
                .map((name) => categoryInfo[name]);

            // Always include "All" option at the beginning
            setPopularCategories([
                {
                    name: "All",
                    color: "bg-slate-500",
                    icon: "🎭",
                    type: "general",
                },
                ...topCategories,
            ]);
        }
    }, [competitions]);

    const handleFilterSelection = (
        category: CategoryInfo,
        onCategoryFilter: (category: string | null) => void
    ) => {
        if (category.name === "All") {
            onCategoryFilter(null);
            return;
        }

        if (filterMode === "category") {
            // When filtering by specific category, pass the category name
            onCategoryFilter(category.name);
        } else {
            // When filtering by type, pass type prefix
            onCategoryFilter(`type:${category.type}`);
        }
    };

    return {
        popularCategories,
        filterMode,
        handleFilterSelection,
        isFilterSelected: (
            category: CategoryInfo,
            selectedCategory: string | null
        ): boolean => {
            return (
                (filterMode === "category" &&
                    selectedCategory === category.name) ||
                (filterMode === "type" &&
                    selectedCategory === `type:${category.type}`) ||
                (selectedCategory === null && category.name === "All")
            );
        },
    };
}
